// components/home/listings/hotel/HotelComponent.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import hotelImagePlaceholder from '../../../../assets/hoteldefault.avif';
import { FaStar, FaMapMarkerAlt, FaArrowRight, FaBed, FaHandPointer } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

/* Construit une URL d'image fiable :
   - chemins stockés avec des backslashes Windows (uploads\\rooms\\...) → normalisés
   - URLs complètes (http) → passées telles quelles
   - vides → placeholder local */
const buildImageUrl = (image) => {
    if (!image) return hotelImagePlaceholder;
    if (image.startsWith('http')) return image;
    const normalized = image.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${normalized}`;
};

const SlickSlider = Slider.default ? Slider.default : Slider;

const HotelComponent = ({ id, name, images, city, description, rating, ratingCount, starRating, rooms, isFirst = false, hintDelay = 500 }) => {
    const { t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const sliderRef = useRef(null);
    const safeImages = (images || []).map(buildImageUrl);
    const hasMultipleImages = safeImages.length > 1;

    /* Indice de première visite : la main ne s'affiche que sur la PREMIÈRE carte,
       une seule fois par navigateur (localStorage). */
    useEffect(() => {
        if (!hasMultipleImages || !isFirst) return;
        if (localStorage.getItem('spotfli_image_hint_shown')) return;
        const timer = setTimeout(() => {
            setShowHint(true);
            localStorage.setItem('spotfli_image_hint_shown', '1');
        }, hintDelay);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Cliquer sur l'image → photo suivante */
    const handleImageClick = () => {
        if (hasMultipleImages && sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };

    // Get the lowest room price
    const lowestPrice = rooms?.reduce((min, room) =>
        room.pricePerNight < min ? room.pricePerNight : min,
        rooms[0]?.pricePerNight || 0
    );

    // Get available room types
    const availableRoomTypes = rooms?.filter(room => room.isAvailable && room.numAvailable > 0)
        .map(room => room.roomType)
        .filter((type, index, arr) => arr.indexOf(type) === index);

    const sliderSettings = {
        dots: true,
        infinite: safeImages.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        beforeChange: (current, next) => setCurrentSlide(next)
    };

    const handleImgError = (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = hotelImagePlaceholder;
    };

    return (
        <div className="hotel-card">
            <div className="hotel-image-slider">
                {hasMultipleImages ? (
                    <SlickSlider ref={sliderRef} {...sliderSettings}>
                        {safeImages.map((image, index) => (
                            <div key={index} className="hotel-slide clickable" onClick={handleImageClick}>
                                <img
                                    src={image}
                                    alt={`${name} - ${index + 1}`}
                                    onError={handleImgError}
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </SlickSlider>
                ) : safeImages.length === 1 ? (
                    <div className="hotel-slide">
                        <img src={safeImages[0]} alt={`${name}`} onError={handleImgError} loading="lazy" />
                    </div>
                ) : (
                    <div className="no-image">
                        <img src={hotelImagePlaceholder} alt="Default hotel" loading="lazy" />
                    </div>
                )}

                {/* Indice de première visite : une main tape l'image (clic = photo suivante) */}
                {showHint && hasMultipleImages && (
                    <div
                        className="hotel-tap-hint"
                        role="status"
                        aria-label={t('hotel_page.click_images_hint')}
                    >
                        <FaHandPointer className="tap-hand" />
                        <span className="tap-ripple" aria-hidden="true" />
                    </div>
                )}

                {/* Badge notation (overlay) */}
                <div className="hotel-rating-badge">
                    <span className="hotel-rating-score">{rating > 0 ? rating.toFixed(1) : 'NR'}</span>
                    <FaStar className="hotel-rating-star" />
                </div>

                {/* Ville en badge sur l'image (coin bas gauche) */}
                {city && (
                    <div className="hotel-city-chip">
                        <FaMapMarkerAlt />
                        <span>{city}</span>
                    </div>
                )}

                {safeImages.length > 1 && (
                    <div className="hotel-slide-count">
                        {currentSlide + 1}/{safeImages.length}
                    </div>
                )}
            </div>

            <div className="hotel-info">
                <div className="hotel-header">
                    <h3 className="hotel-name">{name}</h3>
                    {starRating && starRating > 0 && (
                        <div className="star-rating" aria-label={`${starRating} stars`}>
                            {[...Array(Math.max(0, Math.floor(starRating)))].map((_, i) => (
                                <FaStar key={i} className="star-icon" />
                            ))}
                        </div>
                    )}
                </div>

                {availableRoomTypes && availableRoomTypes.length > 0 && (
                    <div className="available-categories">
                        <FaBed className="available-icon" />
                        <span>
                            <strong>{t('hotel_page.available')}: </strong>
                            {availableRoomTypes.join(', ')}
                        </span>
                    </div>
                )}

                <p className="hotel-description">
                    {description?.length > 110
                        ? `${description.substring(0, 110)}...`
                        : description}
                </p>

                <div className="hotel-footer">
                    <div className="hotel-rating">
                        <span className="rating-text">
                            {rating > 0
                                ? `${ratingCount} ${t('hotel_page.reviews')}`
                                : t('hotel_page.not_rated')}
                        </span>
                    </div>

                    {lowestPrice > 0 && (
                        <div className="hotel-price">
                            <span className="price-from">{t('hotel_page.from')}</span>
                            <span className="price-amount">XAF {lowestPrice.toLocaleString()}</span>
                            <span className="price-night">{t('hotel_page.per_night')}</span>
                        </div>
                    )}
                </div>

                <Link to={`/hotel/${id}`} className="view-hotel-btn">
                    <span>{t('hotel_page.view_hotel')}</span>
                    <FaArrowRight className="view-hotel-arrow" />
                </Link>
            </div>
        </div>
    );
};

export default HotelComponent;
